/**
 * Lot 3.2 — CPF (Mon Compte Formation) : barème importable, codes de certification, lien envoyé à l'apprenant.
 *
 * Porté du premier SaaS (`lib/tarifs-cpf-import.ts` et ses tests) ; la lecture d'un classeur XLSX (archive ZIP) reste
 * côté serveur (`services/cpf.ts`). Module pur : il lit un tableau de cellules, valide et normalise.
 *
 * Règles vérifiées sur sources officielles (DEV_LOG, 30/10/2026) — et seulement celles-là :
 *  - l'inscription se fait sur moncompteformation.gouv.fr (Service-Public, fiche F10705) : un lien envoyé à l'apprenant
 *    ne peut pointer QUE vers ce domaine ;
 *  - les certifications sont enregistrées au RNCP ou au répertoire spécifique (RS) par France Compétences ; leur fiche
 *    publique est francecompetences.fr/recherche/{rncp|rs}/{numéro}/ (vérifié sur la fiche RS6962).
 * Aucun montant réglementaire (participation forfaitaire, plafonds) n'est calculé ici : il évolue (indexation au
 * 1er janvier, projet de loi de finances 2027) et Mon Compte Formation l'affiche lui-même avant validation.
 */

export const EN_TETES_TARIFS_CPF = ["categorie", "intitule", "duree_heures", "prix_euros", "url_moncompteformation"] as const;

export const EXEMPLE_TARIFS_CPF_CSV = [
  EN_TETES_TARIFS_CPF.join(";"),
  "Bureautique & numérique;Tableur (Excel);18;1290;https://www.moncompteformation.gouv.fr/espace-prive/html/#/formation/recherche/00000000000000/00000000000000",
  "Langues;Anglais;28;1990;",
  "Bilan de compétences;Bilan de compétences;24;1800;",
].join("\n");

export const LIGNES_MAX_IMPORT = 5000;

export interface LigneTarifCpf {
  categorie: string;
  intitule: string;
  duree_heures: number;
  /** Prix affiché sur Mon Compte Formation, en centimes. */
  prix_centimes: number;
  url_moncompteformation: string;
}

export interface ResultatLectureTarifs {
  lignes: LigneTarifCpf[];
  erreurs: string[];
  avertissements: string[];
}

type Colonne = (typeof EN_TETES_TARIFS_CPF)[number];

/** Variantes d'en-têtes acceptées (après normalisation : minuscules, sans accents). */
const ALIAS: Record<string, Colonne> = {
  categorie: "categorie",
  category: "categorie",
  intitule: "intitule",
  formation: "intitule",
  titre: "intitule",
  duree_heures: "duree_heures",
  duree: "duree_heures",
  heures: "duree_heures",
  duree_h: "duree_heures",
  prix_euros: "prix_euros",
  prix: "prix_euros",
  tarif: "prix_euros",
  prix_eur: "prix_euros",
  url_moncompteformation: "url_moncompteformation",
  url: "url_moncompteformation",
  lien: "url_moncompteformation",
  lien_moncompteformation: "url_moncompteformation",
};

function normaliserEnTete(valeur: string): string {
  return valeur
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .trim()
    .replace(/\(.*?\)/g, "")
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

/** Découpe un CSV (séparateur « ; » ou « , » détecté sur la 1re ligne, guillemets RFC 4180, BOM toléré). */
export function lireCsv(texte: string): string[][] {
  const contenu = texte.replace(/^\uFEFF/, "");
  const premiere = contenu.split(/\r?\n/, 1)[0] ?? "";
  const separateur = (premiere.match(/;/g)?.length ?? 0) >= (premiere.match(/,/g)?.length ?? 0) ? ";" : ",";
  const lignes: string[][] = [];
  let ligne: string[] = [];
  let champ = "";
  let entreGuillemets = false;
  for (let i = 0; i < contenu.length; i += 1) {
    const c = contenu[i];
    if (entreGuillemets) {
      if (c === '"') {
        if (contenu[i + 1] === '"') {
          champ += '"';
          i += 1;
        } else entreGuillemets = false;
      } else champ += c;
    } else if (c === '"') entreGuillemets = true;
    else if (c === separateur) {
      ligne.push(champ);
      champ = "";
    } else if (c === "\n" || c === "\r") {
      if (c === "\r" && contenu[i + 1] === "\n") i += 1;
      ligne.push(champ);
      lignes.push(ligne);
      ligne = [];
      champ = "";
    } else champ += c;
  }
  if (champ !== "" || ligne.length > 0) {
    ligne.push(champ);
    lignes.push(ligne);
  }
  return lignes.filter((l) => l.some((v) => v.trim() !== ""));
}

/** « 1 290,50 € » → 1290.5 ; NaN si illisible. */
function lireNombre(valeur: string): number {
  const nettoye = valeur.replace(/[\s\u00a0\u202f€]/g, "").replace(",", ".");
  return nettoye === "" ? Number.NaN : Number(nettoye);
}

/** Un lien Mon Compte Formation : https et domaine moncompteformation.gouv.fr (ou un sous-domaine). */
export function estLienMonCompteFormation(url: string): boolean {
  try {
    const u = new URL(url);
    return u.protocol === "https:" && /(^|\.)moncompteformation\.gouv\.fr$/.test(u.hostname);
  } catch {
    return false;
  }
}

/**
 * Convertit un tableau (1re ligne = en-têtes) en lignes de barème validées. Les lignes invalides sont écartées et
 * signalées avec leur numéro (base 1, en-tête compris). Un même couple intitulé × durée présent deux fois : la dernière
 * occurrence l'emporte (avertissement).
 */
export function convertirTarifsCpf(tableau: string[][]): ResultatLectureTarifs {
  const erreurs: string[] = [];
  const avertissements: string[] = [];
  if (tableau.length < 2) return { lignes: [], erreurs: ["Le fichier ne contient aucune ligne de données."], avertissements };

  const positions = new Map<Colonne, number>();
  (tableau[0] ?? []).forEach((entete, index) => {
    const colonne = ALIAS[normaliserEnTete(entete)];
    if (colonne && !positions.has(colonne)) positions.set(colonne, index);
  });
  const manquantes = (["intitule", "duree_heures", "prix_euros"] as const).filter((c) => !positions.has(c));
  if (manquantes.length > 0) return { lignes: [], erreurs: [`Colonne(s) obligatoire(s) absente(s) : ${manquantes.join(", ")}.`], avertissements };

  const parCle = new Map<string, LigneTarifCpf>();
  tableau.slice(1).forEach((cellules, i) => {
    const numero = i + 2;
    const valeur = (c: Colonne) => {
      const index = positions.get(c);
      return index === undefined ? "" : (cellules[index] ?? "").trim();
    };
    const intitule = valeur("intitule").slice(0, 200);
    const duree = lireNombre(valeur("duree_heures"));
    const prix = lireNombre(valeur("prix_euros"));
    const url = valeur("url_moncompteformation");

    if (!intitule) return erreurs.push(`Ligne ${numero} : intitulé manquant.`);
    if (!Number.isInteger(duree) || duree <= 0 || duree > 5000) return erreurs.push(`Ligne ${numero} : durée en heures invalide (entier positif attendu).`);
    if (!Number.isFinite(prix) || prix < 0 || prix > 1_000_000) return erreurs.push(`Ligne ${numero} : prix invalide (nombre positif attendu).`);
    if (url) {
      let protocole: string;
      try {
        protocole = new URL(url).protocol;
      } catch {
        protocole = "";
      }
      if (protocole !== "https:") return erreurs.push(`Ligne ${numero} : lien moncompteformation invalide (https:// attendu).`);
      if (!estLienMonCompteFormation(url)) avertissements.push(`Ligne ${numero} : le lien ne pointe pas vers moncompteformation.gouv.fr ; il ne pourra pas être envoyé à un apprenant.`);
    }

    const cle = `${intitule.toLowerCase()}|${duree}`;
    if (parCle.has(cle)) avertissements.push(`Ligne ${numero} : doublon intitulé × durée, remplace la précédente.`);
    parCle.set(cle, { categorie: valeur("categorie").slice(0, 120) || "Bureautique", intitule, duree_heures: duree, prix_centimes: Math.round(prix * 100), url_moncompteformation: url.slice(0, 1000) });
    return undefined;
  });

  const lignes = Array.from(parCle.values());
  if (lignes.length > LIGNES_MAX_IMPORT) {
    erreurs.push(`Trop de lignes (${lignes.length}) : ${LIGNES_MAX_IMPORT} au maximum par import.`);
    return { lignes: [], erreurs, avertissements };
  }
  return { lignes, erreurs, avertissements };
}

// ——— Certifications (RNCP, répertoire spécifique) ———

export type Repertoire = "RNCP" | "RS";

/** « RS6962 », « rs 6962 », « RNCP-35614 » → { repertoire, numero } ; `null` si ce n'est pas un code de fiche. */
export function analyserCodeCertification(saisie: string): { repertoire: Repertoire; numero: string; code: string } | null {
  const m = /^\s*(RNCP|RS)\s*[- ]?\s*(\d{1,6})\s*$/i.exec(saisie);
  if (!m) return null;
  const repertoire = m[1]!.toUpperCase() as Repertoire;
  const numero = String(Number(m[2]));
  return { repertoire, numero, code: `${repertoire}${numero}` };
}

/** Fiche publique de la certification sur le site de France Compétences. */
export function urlFicheFranceCompetences(code: string): string | null {
  const c = analyserCodeCertification(code);
  return c ? `https://www.francecompetences.fr/recherche/${c.repertoire.toLowerCase()}/${c.numero}/` : null;
}
